import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Modal,
  ModalVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { manageApplicationSagaAction } from "../../../../../../../../redux/saga/manage-application/saga-actions";
import "./AddApplicationModal.scss";

interface ApplicationData {
  project: string;
  technology: number[];
  select_id: "sandbox_application_id" | "production_application_id";
  application_id: string;
  cert: boolean;
  preprod: boolean;
  prod: boolean;
}

const AddApplicationModal: React.FC<{
  applicationData: ApplicationData;
  oldChecks: {
    cert: boolean;
    preprod: boolean;
    prod: boolean;
  };
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clearApplicationData: () => void;
}> = ({ applicationData, oldChecks, setModalOpen, clearApplicationData }) => {
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { status } = useAppSelector((state) => state.manage_application);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();

  const onConfirmButtonClick = () => {
    setSubmitted(true);
    const new_checks = {
      cert: applicationData.cert,
      preprod: applicationData.preprod,
      prod: applicationData.prod,
    };
    dispatch(
      manageApplicationSagaAction({
        application_id: applicationData.application_id.trim(),
        select_id: applicationData.select_id,
        new_checks: new_checks,
        old_checks: oldChecks,
        technology: applicationData.technology,
        project_name: applicationData.project,
        partner_id: partnerDetails.ID,
      })
    );
  };

  const footerConfig = submitted
    ? {}
    : {
        secondaryBtnLabel: "Cancel",
      };

  const onModalClose = () => {
    setModalOpen(false);
    if (submitted && status.manageApplicationStatus) {
      clearApplicationData();
    }
  };

  return (
    <Modal
      headerText="Add Application ID"
      size={ModalVariants.Medium}
      footerConfig={footerConfig}
      onClose={() => onModalClose()}
      className="add-application-modal"
    >
      {submitted ? (
        <div className="status-message-modal-container">
          {status.manageApplicationLoading ? (
            <Loader variant={LoaderSizes.large} />
          ) : status.manageApplicationStatus ? (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <div className="message">Application ID Added Successfully</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={() => onModalClose()}
              />
            </>
          ) : (
            <>
              <Iconography
                icon="circleXmark"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Error}
              />
              <div className="message">{status.manageApplicationMessage}</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={() => setModalOpen(false)}
              />
            </>
          )}
        </div>
      ) : (
        <div className="message-button-container">
          <p className="confirm-message">
            Please confirm that you want to add this application ID
          </p>
          <div className="icon-wrapper">
            <Iconography icon="key" size={IconographySizes.Medium} />
          </div>
          <div className="application-id">{applicationData.application_id}</div>
          <div className="environment-status">
            <div className="status-item">
              <span className="label">CERT:</span>
              <span className="value">
                {applicationData.cert ? "Yes" : "No"}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Preprod:</span>
              <span className="value">
                {applicationData.preprod ? "Yes" : "No"}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Prod:</span>
              <span className="value">
                {applicationData.prod ? "Yes" : "No"}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            label="Confirm and Add Application ID"
            onClick={onConfirmButtonClick}
            disabled={!applicationData.application_id}
          />
        </div>
      )}
    </Modal>
  );
};

export default AddApplicationModal;
